/**
 * Highlights service API calls.
 *
 * This module provides functions for highlight management including
 * creating, retrieving, and deleting text highlights within articles.
 */

import apiClient from './api';

/**
 * Create a new highlight for an article
 * @param {number} articleId - Article ID
 * @param {Object} highlightData - Highlight creation data
 * @param {Object} highlightData.annotation - W3C Web Annotation structure
 * @param {string} highlightData.exact_text - Exact highlighted text
 * @param {string} highlightData.summary_version - SHA-256 hash of summary version
 * @returns {Promise<Object>} Created highlight data
 */
export const createHighlight = async (articleId, highlightData) => {
  try {
    const response = await apiClient.post(
      `/api/articles/${articleId}/highlights`,
      highlightData
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to create highlight'
    );
  }
};

/**
 * Get all highlights for a specific article
 * @param {number} articleId - Article ID
 * @param {boolean} includeDeleted - Whether to include soft-deleted highlights
 * @returns {Promise<Object>} Highlights list response with metadata
 */
export const getArticleHighlights = async (articleId, includeDeleted = false) => {
  try {
    const queryParams = new URLSearchParams({
      include_deleted: includeDeleted.toString(),
    });

    const response = await apiClient.get(
      `/api/articles/${articleId}/highlights?${queryParams}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch highlights'
    );
  }
};

/**
 * Get all highlights for the authenticated user, grouped by article
 * @param {boolean} includeDeleted - Whether to include soft-deleted highlights
 * @returns {Promise<Object>} Grouped highlights response
 */
export const getAllHighlights = async (includeDeleted = false) => {
  try {
    const queryParams = new URLSearchParams({
      include_deleted: includeDeleted.toString(),
    });

    const response = await apiClient.get(`/api/highlights?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch all highlights'
    );
  }
};

/**
 * Delete a highlight (soft delete)
 * @param {number} highlightId - Highlight ID
 * @returns {Promise<void>}
 */
export const deleteHighlight = async (highlightId) => {
  try {
    await apiClient.delete(`/api/highlights/${highlightId}`);
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete highlight'
    );
  }
};

/**
 * Check for detached highlights after summary changes
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Detachment information
 */
export const checkHighlightDetachment = async (articleId) => {
  try {
    const response = await apiClient.get(
      `/api/articles/${articleId}/highlights/detachment-info`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to check highlight detachment'
    );
  }
};

/**
 * Re-attach detached highlights after summary changes
 * @param {number} articleId - Article ID
 * @param {number} similarityThreshold - Minimum similarity threshold (0.0-1.0)
 * @returns {Promise<Object>} Re-attachment results
 */
export const reattachHighlights = async (articleId, similarityThreshold = 0.8) => {
  try {
    const response = await apiClient.post(
      `/api/articles/${articleId}/highlights/reattach`,
      { similarity_threshold: similarityThreshold }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to re-attach highlights'
    );
  }
};

/**
 * Delete all highlights for an article
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Deletion results
 */
export const deleteAllHighlights = async (articleId) => {
  try {
    const response = await apiClient.delete(
      `/api/articles/${articleId}/highlights`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete all highlights'
    );
  }
};
